import lodash from 'lodash'
import type { IDB_TableName } from './IDB.TableName'
import { Logg_event } from '../Logg/Logg.event'
import { Logg_timer } from '../Logg/Logg.timer'
import { sort, uniq } from '../../../shared/lib/functional/array.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'

export function IDB_transaction(
    db: IDBDatabase,
    storeNames: IDB_TableName[],
    mode: 'readonly' | 'readwrite',
    onError: (error: Error) => void,
    onSuccess: (transaction: IDBTransaction) => void
) {
    try {
        const tr = db.transaction(storeNames, mode)
        tr.onerror = () => onError(tr.error)
        onSuccess(tr)
        tr.onerror = null
        onError = null
        onSuccess = null
    } catch (error) {
        onError(error)
        onError = null
        onSuccess = null
    }
}

export function IDB_rowsWrite<T>(db: IDBDatabase, objectStoreName: IDB_TableName, rows: T[]): Promise<void> {
    return new Promise((resolve, reject) => {
        if (lodash.isEmpty(rows)) {
            resolve()
            return
        }

        const timer = Logg_timer()

        IDB_transaction(db, [objectStoreName], 'readwrite', reject, (tr) => {
            tr.oncomplete = () => {
                Logg_event('IDBWrite', { duration: timer.getDurationInWholeSeconds(), objectStoreName })
                resolve()
            }
            const objectStore = tr.objectStore(objectStoreName)

            for (const row of rows) {
                objectStore.put(row)
            }
        })
    })
}

export function IDB_rowWrite<T>(db: IDBDatabase, objectStoreName: IDB_TableName, row: T) {
    return new Promise<number>((resolve, reject) => {
        IDB_transaction(db, [objectStoreName], 'readwrite', reject, (tr) => {
            tr.objectStore(objectStoreName).put(row).onsuccess = (event: any) => resolve(event.target.result)
        })
    })
}

export function IDB_rowRead<T>(db: IDBDatabase, objectStoreName: IDB_TableName, keyPath: any) {
    return new Promise<T>((resolve, reject) =>
        IDB_transaction(db, [objectStoreName], 'readonly', reject, (tr) => {
            tr.objectStore(objectStoreName).get(keyPath).onsuccess = (event: any) => resolve(event.target.result)
        })
    )
}

export function IDB_rowsReadAll<T>(db: IDBDatabase, objectStoreName: IDB_TableName) {
    return new Promise<T[]>((resolve, reject) =>
        IDB_transaction(db, [objectStoreName], 'readonly', reject, (tr) => {
            let rows: T[] = []

            tr.objectStore(objectStoreName).openCursor().onsuccess = (evt: any) => {
                const cursor = evt.target.result
                if (cursor) {
                    rows.push(cursor.value)
                    cursor.continue()
                } else {
                    resolve(rows)
                    // Please garbage collect
                    rows = null
                    resolve = null
                    reject = null
                }
            }
        })
    )
}

export async function IDB_rowsReadSome<T>(db: IDBDatabase, objectStoreName: IDB_TableName, keyPaths: any[]) {
    // If empty, then return empty
    if (keyPaths.length === 0) return []

    keyPaths = pipe(
        // Sort keypaths in the same order as the indexes
        sort(keyPaths, (a, b) => indexedDB.cmp(a, b)),
        uniq
    )

    // Create idb range
    const lowerBound = keyPaths[0]
    const upperBound = keyPaths[keyPaths.length - 1]
    const range = IDBKeyRange.bound(lowerBound, upperBound)

    return new Promise<T[]>((resolve, reject) =>
        IDB_transaction(db, [objectStoreName], 'readonly', reject, (tr) => {
            let rows: T[] = []

            tr.objectStore(objectStoreName).openCursor(range).onsuccess = (evt: any) => {
                const cursor = evt.target.result
                if (cursor) {
                    // As the cursor.continue will go past keyPaths if it can't find them
                    // The current key could be anyone of the keys we're looking for
                    if (keyPaths.includes(cursor.key)) {
                        rows.push(cursor.value)
                    }

                    // Remove all the keys that we've passed
                    keyPaths = keyPaths.filter((k) => indexedDB.cmp(k, cursor.key) > 0)

                    // If there are more key paths to look for
                    if (keyPaths.length > 0) {
                        cursor.continue(keyPaths[0])
                        return
                    }
                }

                resolve(rows)
                // Please garbage collect
                rows = null
                resolve = null
                reject = null
            }
        })
    )
}

export function IDB_idsReadAll<Id>(db: IDBDatabase, objectStoreName: IDB_TableName) {
    return new Promise<Id[]>((resolve, reject) =>
        IDB_transaction(db, [objectStoreName], 'readonly', reject, (tr) => {
            tr.objectStore(objectStoreName).getAllKeys().onsuccess = (evt: any) => {
                resolve(evt.target.result)
            }
        })
    )
}

export function IDB_rowsDelete(db: IDBDatabase, objectStoreName: IDB_TableName, keyPaths: any[]) {
    return new Promise<void>((resolve, reject) =>
        IDB_transaction(db, [objectStoreName], 'readwrite', reject, (tr) => {
            tr.objectStore(objectStoreName).openCursor().onsuccess = (evt: any) => {
                const cursor: IDBCursorWithValue = evt.target.result
                if (cursor) {
                    if (keyPaths.indexOf(cursor.key) >= 0) {
                        cursor.delete()
                    }
                    cursor.continue()
                } else {
                    resolve()
                }
            }
        })
    )
}

export function IDB_dictWrite<T>(db: IDBDatabase, objectStoreName: IDB_TableName, dict: { [key: string]: T }) {
    return new Promise<void>((resolve, reject) => {
        if (lodash.isEmpty(dict)) {
            resolve()
            return
        }

        const timer = Logg_timer()

        IDB_transaction(db, [objectStoreName], 'readwrite', reject, (tr) => {
            tr.oncomplete = () => {
                Logg_event('IDBWrite', {
                    duration: timer.getDurationInWholeSeconds(),
                    objectStoreName,
                })
                resolve()
            }

            const objectStore = tr.objectStore(objectStoreName)

            for (const key in dict) {
                objectStore.put(dict[key], key)
            }
        })
    })
}

export function IDB_dictReadAll<T>(db: IDBDatabase, objectStoreName: IDB_TableName) {
    return new Promise<{ [key: string]: T }>((resolve, reject) =>
        IDB_transaction(db, [objectStoreName], 'readonly', reject, (tr) => {
            let dict: { [key: string]: T } = {}

            tr.objectStore(objectStoreName).openCursor().onsuccess = (evt: any) => {
                const cursor = evt.target.result
                if (cursor) {
                    dict[cursor.key] = cursor.value
                    cursor.continue()
                } else {
                    resolve(dict)
                    // Please garbage collect
                    dict = null
                    resolve = null
                    reject = null
                }
            }
        })
    )
}

export function IDB_keysReadAll<T>(db: IDBDatabase, objectStoreName: IDB_TableName) {
    return new Promise<T[]>((resolve, reject) =>
        IDB_transaction(db, [objectStoreName], 'readonly', reject, (tr) => {
            tr.objectStore(objectStoreName).getAllKeys().onsuccess = (evt: any) => {
                resolve(evt.target.result)
            }
        })
    )
}

export function IDB_deleteAll(db: IDBDatabase, objectStoreName: IDB_TableName) {
    return new Promise((resolve, reject) =>
        IDB_transaction(db, [objectStoreName], 'readwrite', reject, (tr) => {
            tr.objectStore(objectStoreName).clear().onsuccess = resolve
        })
    )
}
